'use client';

import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { useClientValue } from '@/hooks/useClientValue';

type UserAgentProviderProps = {
    children: ReactNode;
};

type userAgentContext = {
    isMobile: boolean;
};

const UserAgentContext = createContext<userAgentContext | undefined>({
    isMobile: false
});

/** Module scope so the reference is stable across renders — see {@link useClientValue}. */
const readIsMobile = () => /Mobi|Android/i.test(navigator.userAgent);

/**
 * Whether this is a mobile device, resolved in the browser rather than from the `user-agent` header
 * so the HTML does not differ per device. Desktop is the answer on the server and through
 * hydration; a phone gets the mobile branch one paint later.
 */
export const UserAgentProvider: React.FC<UserAgentProviderProps> = ({ children }) => {
    const isMobile = useClientValue(readIsMobile, false);
    const context = useMemo(() => ({ isMobile: isMobile }), [isMobile]);

    return <UserAgentContext.Provider value={context}>{children}</UserAgentContext.Provider>;
};

export const useUserAgent = (): userAgentContext => {
    const context = useContext(UserAgentContext);
    if (context === undefined) {
        throw new Error('useUserAgent must be used within a UserAgentProvider');
    }
    return context;
};
